import {
  Box,
  Flex,
  Icon,
  Skeleton,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Text,
  useMultiStyleConfig,
  useTab,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import { FiCircle, FiCheckCircle } from 'react-icons/fi';
import { useFundingRequestContext } from '~/borrower/domains/fundingRequests/ui/manage/Context';
import { FundingRequestMonitorViewBody } from '~/borrower/domains/fundingRequests/ui/manage/Monitor';
import { FundingRequestPlanViewBody } from '~/borrower/domains/fundingRequests/ui/manage/Plan';
import { FundingRequestReviewViewBody } from '~/borrower/domains/fundingRequests/ui/manage/Review';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';

import 'source/borrower/borrower.scss';

export type FundingRequestManageStep = 'modify' | 'review' | 'monitor';
export const isFundingRequestManageStep = (val: any) => {
  return val && (val === 'modify' || val === 'review' || val === 'monitor');
};

const TAB_PLAN = 0;
const TAB_REVIEW = 1;
const TAB_MONITOR = 2;

/**
 * Calculate which manage step we need to display here.
 * We will consider 'desiredStep', however, FundingRequest status can ultimately override any desiredStep
 * if status does not allow navigating to such step tabIndex.
 */
const getTabIndex = (
  status: string,
  desiredStep: FundingRequestManageStep | null,
): number => {
  if (status === 'pending') {
    // In `pending' status, we can honor following desiredStep
    switch (desiredStep) {
      case 'review':
        return TAB_REVIEW;
      case 'modify':
      default:
        return TAB_PLAN;
    }
  }

  // All other cases, we will force them to `monitor` tab
  return TAB_MONITOR;
};

export const FundingRequestManageViewContainer = ({
  desiredStepDefault,
}: {
  desiredStepDefault: FundingRequestManageStep | null;
}) => {
  const {
    error,
    fundingRequestId,
    isLoading,
    state,
  } = useFundingRequestContext();
  const { setCreditFacilityId } = useCreditFacilityContext();
  const { _status, _reportingDate, _fileVersion, creditFacilityId } = state;

  useEffect(() => {
    if (creditFacilityId) {
      setCreditFacilityId(creditFacilityId);
    }
  }, [creditFacilityId, setCreditFacilityId]);

  const [desiredStep, setDesiredStep] = useState(desiredStepDefault);

  // Specific send to Review Tab (used in continue button)
  const toReviewTab = () => {
    setDesiredStep('review');
  };

  const toPlanTab = () => {
    setDesiredStep('modify');
  };

  const toMonitorTab = () => {
    setDesiredStep('monitor');
  };

  if (isLoading) {
    return <Skeleton data-testid="loading-funding-request" height="600px" />;
  }
  if (error) {
    return <Navigate to="/funding-requests" />;
  }

  const activeIndex = getTabIndex(_status, desiredStep);

  const FundingRequestTab = React.forwardRef<
    HTMLInputElement,
    {
      label: string;
      tabNumber: number;
      activeIndex: number;
    }
  >(({ label, ...props }, ref) => {
    // 1. Reuse the `useTab` hook
    const tabProps: any = useTab({ label, ref });

    const styleSelected = {
      fontWeight: 'bold',
      borderBottom: '2px',
      backgroundColor: 'green.100',
      color: 'green.400',
    };

    // 2. Hook into the Tabs `size`, `variant`, props
    const styles = useMultiStyleConfig('Tabs', tabProps);

    const { tabNumber } = props;
    let iconImage = activeIndex >= tabNumber ? FiCheckCircle : FiCircle;
    // these icon props only when
    const submittedMonitorIconProps: {
      color?: string;
      backgroundColor?: string;
      borderRadius?: string;
    } = {};
    if (
      activeIndex === TAB_MONITOR &&
      tabNumber === TAB_MONITOR &&
      _status === 'submitted'
    ) {
      iconImage = FiCircle;
      styleSelected.backgroundColor = '#F2F2F2';
      styleSelected.color = '#000000';
      styleSelected.borderBottom = '2px solid #F2B824';
      submittedMonitorIconProps.color = '#CAA371';
      submittedMonitorIconProps.backgroundColor = '#F2B824';
      submittedMonitorIconProps.borderRadius = '10px';
    }

    return (
      <Box
        fontSize={{ base: '12px', sm: '12px', md: '16px' }}
        _hover={{ cursor: 'default' }}
        __css={styles.tab}
        _selected={styleSelected}
        {...tabProps}
        padding="15px 50px"
      >
        <Flex position="relative">
          <Icon
            marginRight="6px"
            height="auto"
            position="absolute"
            top="4px"
            left="-21px"
            as={iconImage}
            {...submittedMonitorIconProps}
          />
          <Text>{label}</Text>
        </Flex>
      </Box>
    );
  });

  let borrowingBaseReportDetails;
  if (!_.isNil(_reportingDate) && !_.isNil(_fileVersion)) {
    let detailsLabel = `Based on the borrowing base report of ${commonDate.fullDateStrToHumanDateString(
      _reportingDate,
    )}`;
    if (_fileVersion !== 1) {
      detailsLabel = `${detailsLabel}, file version v${_fileVersion}`;
    }
    borrowingBaseReportDetails = (
      <Text fontStyle="italic" fontSize="sm">
        {detailsLabel}
      </Text>
    );
  }

  return (
    <Box>
      <Box mb="8px" pb="15px">
        <Text fontSize="md">Funding Request ID: {fundingRequestId}</Text>
        {borrowingBaseReportDetails}
      </Box>
      <Tabs variant="line" index={activeIndex}>
        <TabList>
          <FundingRequestTab
            tabNumber={TAB_PLAN}
            activeIndex={activeIndex}
            label="Plan funding request"
          />
          <FundingRequestTab
            tabNumber={TAB_REVIEW}
            activeIndex={activeIndex}
            label="Review funding request"
          />
          <FundingRequestTab
            tabNumber={TAB_MONITOR}
            activeIndex={activeIndex}
            label="Monitor request status"
          />
        </TabList>
        <Box height="20px" />
        <TabPanels>
          <TabPanel pl="0">
            <FundingRequestPlanViewBody
              continueFn={toReviewTab}
              isActive={TAB_PLAN === activeIndex}
            />
          </TabPanel>
          <TabPanel pl="0">
            <FundingRequestReviewViewBody
              toPlanTab={toPlanTab}
              toMonitorTab={toMonitorTab}
            />
          </TabPanel>
          <TabPanel pl="0">
            <FundingRequestMonitorViewBody />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
